/**
 * RBACService - Frontend API client for RBAC endpoints
 */

import { apiService } from './ApiService';

export interface Role {
  id: string;
  name: string;
  name_he: string | null;
  description: string | null;
  is_system_role: boolean;
  is_site_scoped: boolean;
  hierarchy_level: number;
}

export interface UserRole {
  assignment_id: number;
  role_id: string;
  site_id: string | null;
  role_name: string;
  role_name_he: string | null;
  hierarchy_level: number;
  is_site_scoped: boolean;
  site_name: string | null;
  granted_at: string;
  expires_at: string | null;
  is_active: boolean;
  granted_by_name: string | null;
}

export interface Permission {
  permission_id: string;
  permission_name: string;
  resource_type: string;
  action: string;
  is_site_scoped: boolean;
}

export interface SiteUser {
  user_id: string;
  user_name: string;
  email: string;
  role_id: string;
  role_name: string;
  role_name_he: string | null;
  granted_at: string;
  expires_at: string | null;
}

export interface POIFieldPermissions {
  allowedFields: string[];
  canCreatePoi: boolean;
}

export interface UserSites {
  allSites: boolean;
  sites: Array<{
    id: string;
    name: string;
    description: string | null;
    created_at: string;
  }>;
}

export interface RBACSummary {
  userId: string;
  roles: UserRole[];
  permissions: string[];
  isAdmin: boolean;
  /**
   * Sites where this person holds site_admin. Replaces `isAccountAdmin`, a site-agnostic
   * boolean naming a role that stopped being global in migration 020 and stopped existing in
   * 068. `sitePermissions` is still returned by the server when a siteId is passed, and is
   * deliberately not typed here: nothing in the app passes one.
   */
  adminSiteIds: string[];
  /**
   * May this person create sites — a platform admin, or someone Idan put in site_creators
   * (migration 072). Not a role: it is never listed or granted on the Users & Groups screen.
   */
  canCreateSites?: boolean;
}

class RBACService {
  // ============================================================================
  // ROLES
  // ============================================================================

  /**
   * Get all available roles
   */
  async getRoles(): Promise<Role[]> {
    return apiService.get<Role[]>('/rbac/roles');
  }

  // ============================================================================
  // CURRENT USER
  // ============================================================================

  /**
   * Get current user's roles
   */
  async getMyRoles(): Promise<UserRole[]> {
    return apiService.get<UserRole[]>('/rbac/my-roles');
  }

  /**
   * Get current user's permissions
   */
  async getMyPermissions(siteId?: string): Promise<Permission[]> {
    const params = siteId ? `?siteId=${siteId}` : '';
    return apiService.get<Permission[]>(`/rbac/my-permissions${params}`);
  }

  /**
   * Get sites current user has access to
   */
  async getMySites(): Promise<UserSites> {
    return apiService.get<UserSites>('/rbac/my-sites');
  }

  /**
   * Get complete RBAC summary for current user
   */
  async getMySummary(siteId?: string): Promise<RBACSummary> {
    const params = siteId ? `?siteId=${siteId}` : '';
    return apiService.get<RBACSummary>(`/rbac/my-summary${params}`);
  }

  // ============================================================================
  // USER ROLE MANAGEMENT
  // ============================================================================

  /**
   * Get a user's roles (requires account_admin or admin)
   */
  async getUserRoles(userId: string): Promise<UserRole[]> {
    return apiService.get<UserRole[]>(`/rbac/users/${userId}/roles`);
  }

  /**
   * Assign a role to a user
   */
  async assignRole(
    userId: string,
    roleId: string,
    siteId?: string,
    options?: { notes?: string; expiresAt?: string }
  ): Promise<void> {
    await apiService.post(`/rbac/users/${userId}/roles`, {
      roleId,
      siteId,
      ...options
    });
  }

  /**
   * Revoke a role from a user
   */
  async revokeRole(userId: string, roleId: string, siteId?: string): Promise<void> {
    const params = siteId ? `?siteId=${siteId}` : '';
    await apiService.delete(`/rbac/users/${userId}/roles/${roleId}${params}`);
  }

  // ============================================================================
  // SITE USER MANAGEMENT
  // ============================================================================

  /**
   * Get all users with access to a site
   */
  async getSiteUsers(siteId: string): Promise<SiteUser[]> {
    return apiService.get<SiteUser[]>(`/rbac/sites/${siteId}/users`);
  }

  // ============================================================================
  // POI FIELD PERMISSIONS
  // ============================================================================

  /**
   * Get POI field permissions for a user on a site
   */
  async getFieldPermissions(siteId: string, userId: string): Promise<POIFieldPermissions | null> {
    return apiService.get<POIFieldPermissions | null>(
      `/rbac/sites/${siteId}/field-permissions/${userId}`
    );
  }

  /**
   * Set POI field permissions for a user on a site
   */
  async setFieldPermissions(
    siteId: string,
    userId: string,
    allowedFields: string[],
    canCreatePoi: boolean
  ): Promise<void> {
    await apiService.post(`/rbac/sites/${siteId}/field-permissions/${userId}`, {
      allowedFields,
      canCreatePoi
    });
  }

  /**
   * Remove POI field permissions for a user on a site
   */
  async removeFieldPermissions(siteId: string, userId: string): Promise<void> {
    await apiService.delete(`/rbac/sites/${siteId}/field-permissions/${userId}`);
  }

  // ============================================================================
  // PERMISSION CHECKS
  // ============================================================================

  /**
   * Check if current user has a specific permission
   */
  async checkPermission(permissionId: string, siteId?: string): Promise<boolean> {
    const result = await apiService.post<{ hasPermission: boolean }>('/rbac/check-permission', {
      permissionId,
      siteId
    });
    return result.hasPermission;
  }

  /**
   * Check if current user can edit a specific POI field
   */
  async checkPOIField(siteId: string, fieldName: string): Promise<boolean> {
    const result = await apiService.post<{ canEdit: boolean }>('/rbac/check-poi-field', {
      siteId,
      fieldName
    });
    return result.canEdit;
  }

  /**
   * Check if current user can create POIs on a site
   */
  async checkPOICreate(siteId: string): Promise<boolean> {
    const result = await apiService.post<{ canCreate: boolean }>('/rbac/check-poi-create', {
      siteId
    });
    return result.canCreate;
  }
}

export default new RBACService();
